import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  RefreshCw,
  TrendingUp,
  Activity,
  UserPlus,
  Hourglass,
  Truck,
  XCircle,
  CheckCircle2,
  CreditCard,
  AlertCircle,
  Layers,
  Calendar,
} from "lucide-react";

import { api, formatApiError } from "@/shared/lib/api";
import AdminShell from "@/apps/admin/v2/AdminShell";
import { Card, CardHeader, KpiCard, PillButton } from "@/apps/admin/v2/components/Card";

const KIND_ICON = {
  rider_registered: UserPlus,
  driver_registered: Truck,
  waitlist_signup: Hourglass,
  waitlist_approved: CheckCircle2,
  waitlist_rejected: XCircle,
  trip_booked: Calendar,
  trip_cancelled: XCircle,
  trip_completed: CheckCircle2,
  block_created: Layers,
  block_claimed: Truck,
  block_completed: CheckCircle2,
  payment_paid: CreditCard,
  payment_failed: AlertCircle,
};

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diffMin = Math.round((Date.now() - d.getTime()) / 60000);
    if (diffMin < 1) return "just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffMin < 60 * 24) return `${Math.round(diffMin / 60)}h ago`;
    return d.toLocaleDateString();
  } catch {
    return iso;
  }
}

export default function CommandCenter() {
  const [market, setMarket] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [ops, setOps] = useState(null);
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastLoaded, setLastLoaded] = useState(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [m, r, o, a] = await Promise.all([
        api.get("/admin/v2/command/marketplace-health"),
        api.get("/admin/v2/command/revenue-health"),
        api.get("/admin/v2/command/operations-health"),
        api.get("/admin/v2/activity?limit=40"),
      ]);
      setMarket(m.data);
      setRevenue(r.data);
      setOps(o.data);
      setFeed(a.data.events || []);
      setLastLoaded(new Date());
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load command center");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Live polling — 5s for activity feed only, 30s for KPIs.
  useEffect(() => {
    const fast = setInterval(() => {
      api.get("/admin/v2/activity?limit=40").then((r) => setFeed(r.data.events || [])).catch(() => {});
    }, 5000);
    const slow = setInterval(() => load(true), 30000);
    return () => { clearInterval(fast); clearInterval(slow); };
  }, [load]);

  const cards = market?.cards || [];
  const revCards = useMemo(() => {
    if (!revenue) return [];
    return [
      { key: "today", label: "Revenue Today", value: `$${revenue.stripe_paid.today.toFixed(2)}`, state: "healthy" },
      { key: "week", label: "Revenue Week", value: `$${revenue.stripe_paid.week.toFixed(2)}`, state: "healthy" },
      { key: "month", label: "Revenue Month", value: `$${revenue.stripe_paid.month.toFixed(2)}`, state: "healthy" },
      { key: "lifetime", label: "Lifetime", value: `$${revenue.stripe_paid.lifetime.toFixed(2)}`, state: "healthy" },
      { key: "expected", label: "Expected (quoted)", value: `$${revenue.expected_quoted_month.toFixed(2)}`, state: "watch", beta: true, betaNote: "From pricing_snapshots; not yet collected via Stripe." },
      { key: "pending_payouts", label: "Pending Payouts", value: `$${revenue.pending_driver_payouts.toFixed(2)}`, state: "watch" },
      { key: "platform_margin", label: "Platform Margin", value: `$${revenue.platform_margin.toFixed(2)}`, state: "healthy" },
      { key: "failed", label: "Failed Payments", value: revenue.failed_payments.failures_30d_log, state: revenue.failed_payments.failures_30d_log > 0 ? "watch" : "healthy", suffix: "/30d" },
    ];
  }, [revenue]);

  const opsCards = useMemo(() => {
    if (!ops) return [];
    return [
      { key: "completed", label: "Completed Today", value: ops.completed_trips_today, state: "healthy" },
      { key: "active", label: "Active Trips", value: ops.active_trips, state: ops.active_trips > 0 ? "healthy" : "watch" },
      { key: "cancelled", label: "Cancellations", value: ops.cancelled_today, state: ops.cancelled_today === 0 ? "healthy" : "watch" },
      { key: "scheduled", label: "Scheduled Today", value: ops.scheduled_today, state: "healthy" },
      { key: "completion", label: "Completion Rate", value: ops.completion_rate_pct, suffix: "%", state: ops.completion_rate_pct >= 80 ? "healthy" : "watch" },
      { key: "fill", label: "Block Fill Rate", value: ops.block_fill_rate_pct, suffix: "%", state: ops.block_fill_rate_pct >= 50 ? "healthy" : "watch" },
      { key: "util", label: "Driver Utilization", value: ops.driver_utilization_pct, suffix: "%", state: "watch" },
      { key: "ontime", label: "On-Time Pickup", value: null, suffix: "%", state: "watch", beta: true, betaNote: ops.on_time_pickup_pct?.beta_note },
    ];
  }, [ops]);

  return (
    <AdminShell
      title="Command Center"
      subtitle="Real-time picture of marketplace, revenue, and operations — every number traces back to live data."
      headerActions={
        <>
          {lastLoaded && (
            <div className="text-[10px] tracking-[0.18em] uppercase text-[#475569]" data-testid="cmd-last-loaded">
              live · {lastLoaded.toLocaleTimeString()} · auto 5s/30s
            </div>
          )}
          <PillButton onClick={() => load(false)} disabled={loading} variant="secondary" testid="cmd-refresh">
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
          </PillButton>
        </>
      }
    >
      {/* Marketplace Health */}
      <section className="mb-8" data-testid="cmd-marketplace-health">
        <SectionTitle eyebrow="Pillar 01" title="Marketplace Health" icon={<Activity size={14} />} />
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
          {cards.map((c) => (
            <KpiCard
              key={c.key}
              label={c.label}
              value={c.value}
              suffix={c.suffix}
              state={c.state}
              delta={c.delta_today}
              beta={c.beta}
              betaNote={c.beta_note}
              testid={`cmd-kpi-${c.key}`}
            />
          ))}
        </div>
      </section>

      {/* Revenue Health */}
      <section className="mb-8" data-testid="cmd-revenue-health">
        <SectionTitle eyebrow="Pillar 02" title="Revenue Health" icon={<TrendingUp size={14} />} />
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
          {revCards.map((c) => (
            <KpiCard
              key={c.key}
              label={c.label}
              value={c.value}
              suffix={c.suffix}
              state={c.state}
              beta={c.beta}
              betaNote={c.betaNote}
              testid={`cmd-rev-${c.key}`}
            />
          ))}
        </div>
      </section>

      {/* Operations Health */}
      <section className="mb-8" data-testid="cmd-operations-health">
        <SectionTitle eyebrow="Pillar 03" title="Operations Health" icon={<Truck size={14} />} />
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
          {opsCards.map((c) => (
            <KpiCard
              key={c.key}
              label={c.label}
              value={c.value}
              suffix={c.suffix}
              state={c.state}
              beta={c.beta}
              betaNote={c.betaNote}
              testid={`cmd-ops-${c.key}`}
            />
          ))}
        </div>
      </section>

      {/* Live Activity Feed */}
      <section className="grid grid-cols-1 xl:grid-cols-3 gap-4" data-testid="cmd-activity">
        <Card className="xl:col-span-2">
          <CardHeader
            eyebrow="Pillar 04"
            title="Live Activity"
            testid="cmd-activity-header"
            action={
              <span className="text-[10px] tracking-[0.2em] uppercase text-[#475569]">
                {feed.length} events · poll 5s
              </span>
            }
          />
          <div className="px-2 py-3 max-h-[480px] overflow-y-auto" data-testid="cmd-activity-feed">
            {feed.length === 0 ? (
              <div className="text-center py-12 text-[#475569] text-[13px]">
                No activity in the last 24h.
              </div>
            ) : feed.map((e, i) => {
              const Icon = KIND_ICON[e.kind] || Activity;
              const tone = e.kind === "payment_failed" || e.kind === "trip_cancelled"
                ? "text-red-600"
                : e.kind === "payment_paid" || e.kind === "trip_completed" || e.kind === "block_completed"
                ? "text-emerald-600"
                : "text-[#081B4B]";
              return (
                <div
                  key={`${e.ref_id}-${i}`}
                  className="flex items-start gap-3 px-3 py-2.5 hover:bg-[#F4F4F6] rounded-lg"
                  data-testid={`cmd-activity-row-${i}`}
                >
                  <div className={`mt-0.5 ${tone}`}><Icon size={14} strokeWidth={1.8} /></div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[12px] font-medium text-[#081B4B]">{e.title}</div>
                    <div className="text-[11px] text-[#475569] truncate">{e.subtitle}</div>
                  </div>
                  <div className="text-[10px] text-[#475569] tracking-wider whitespace-nowrap pt-1">{fmtTime(e.ts)}</div>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Quick links */}
        <Card>
          <CardHeader eyebrow="Jump to" title="Operational shortcuts" />
          <div className="p-4 space-y-2">
            <QuickLink to="/admin/v2/marketplace" label="Marketplace map & sectors" sub="Coverage heatmap · sector performance · block generator" />
            <QuickLink to="/admin/v2/operations" label="Live ride board" sub="Dispatch · trip status · alerts" />
            <QuickLink to="/admin/v2/finance" label="Finance" sub="Revenue · payouts · Stripe analytics" />
            <QuickLink to="/admin/v2/drivers" label="Drivers" sub="Directory · compliance · performance" />
            <QuickLink to="/admin/v2/settings/sectors" label="Sector Manager" sub="Draw / edit / merge sector boundaries" />
            <div className="pt-2 border-t border-[#0F172A]/[0.06] mt-2">
              <QuickLink to="/admin/home" label="Legacy admin home" sub="Old Control Room (preserved)" muted />
              <QuickLink to="/admin/blocks" label="Legacy block dashboard" sub="Phase 3 blocks UI (still available)" muted />
            </div>
          </div>
        </Card>
      </section>
    </AdminShell>
  );
}

function SectionTitle({ eyebrow, title, icon }) {
  return (
    <div className="flex items-baseline gap-3 mb-3">
      <span className="text-[10px] tracking-[0.3em] uppercase text-[#F97316] font-semibold">{eyebrow}</span>
      <div className="flex items-center gap-2 text-[#081B4B]">
        {icon}
        <span className="font-serif text-[20px]">{title}</span>
      </div>
    </div>
  );
}

function QuickLink({ to, label, sub, muted }) {
  return (
    <Link
      to={to}
      className={`block rounded-xl border border-[#0F172A]/[0.08] p-3 hover:border-[#F97316]/40 hover:bg-[#FFF7ED]/50 transition-colors ${muted ? "opacity-70" : ""}`}
      data-testid={`cmd-quick-${to.split("/").pop()}`}
    >
      <div className="text-[13px] font-semibold text-[#081B4B]">{label}</div>
      <div className="text-[11px] text-[#475569] mt-0.5">{sub}</div>
    </Link>
  );
}
